<template>
    <div class="border-b rounded-lg border-theme-border flex flex-wrap">
        <div
            class="lg:w-2/12 md:w-1/4 w-4/12 hidden md:flex justify-center items-center py-3"
        >
            <div class="w-28 h-28 p-0">
                <img
                    v-img-loading
                    class="w-full h-full"
                    :src="reference.images[0].url | image"
                    :alt="reference.images[0].alt"
                    loading="lazy"
                />
            </div>
        </div>
        <div
            class="lg:w-10/12 md:w-3/4 w-full flex flex-col justify-around py-3 px-1"
        >
            <div class="flex justify-between">
                <div class="flex flex-col">
                    <strong>
                        {{ reference.product.name }}
                    </strong>
                    <small>
                        Prod
                        <router-link
                            :to="`/app/products/detail/${reference.productId}`"
                            class="btn-a p-0"
                        >
                            {{
                                reference.product.sku
                                    ? `SKU ${reference.product.sku}`
                                    : `ID ${reference.productId}`
                            }}
                        </router-link>
                        | Ref
                        <router-link
                            :to="`/app/products/detail/${reference.productId}?reference=${reference.id}&tab=references`"
                            class="btn-a p-0"
                        >
                            {{
                                reference.barcode
                                    ? `Cód. ${reference.barcode}`
                                    : `ID ${reference.id}`
                            }}
                        </router-link>
                    </small>
                </div>
                <div v-if="enabled" id="actions" class="flex">
                    <app-button danger icon @click="remove">🗑</app-button>
                </div>
            </div>

            <div class="block md:hidden w-28 h-28 p-0 mx-auto mt-3">
                <img
                    v-img-loading
                    class="w-full h-full"
                    :src="reference.images[0].url | image"
                    :alt="reference.images[0].alt"
                    loading="lazy"
                />
            </div>

            <div class="flex flex-wrap mt-3">
                <div class="w-1/2 lg:w-4/12 flex flex-col">
                    <strong class="text-sm">Color </strong>
                    <div class="flex items-center">
                        <small class="capitalize">
                            {{ reference.color }}
                        </small>
                        <div
                            class="ml-1 w-5 h-5 rounded-full border border-theme-border"
                            :style="{
                                'background-color': reference.colorRef
                            }"
                        ></div>
                    </div>
                </div>
                <div class="w-1/2 lg:w-4/12 flex flex-col">
                    <strong class="text-sm">Tamaño </strong>
                    <span class="capitalize">
                        {{ reference.size }}
                    </span>
                </div>
                <div class="w-1/2 lg:w-4/12 mt-3 flex flex-col">
                    <strong class="text-sm">U. Actuales </strong>
                    <span>
                        {{ reference.stock }}
                    </span>
                </div>
                <div class="w-1/2 lg:w-4/12 mt-3 flex flex-col">
                    <strong class="text-sm">Precio actual u. </strong>
                    <small>
                        {{ reference.priceOffer | money }}
                    </small>
                </div>
                <div class="w-1/2 lg:w-4/12 mt-3 flex flex-col">
                    <strong class="text-sm">Costo actual u. </strong>
                    <small>
                        {{ reference.cost | money }}
                    </small>
                </div>
                <div class="w-1/2 lg:w-4/12 mt-3 flex flex-col">
                    <strong class="text-sm">Margen bruto actual </strong>
                    <small>
                        {{ revenueCurrent | percent }}
                    </small>
                </div>
                <div class="w-1/2 lg:w-4/12 mt-3 flex flex-col">
                    <strong class="text-sm">
                        {{
                            isInputCostUnit
                                ? "Costo de lote"
                                : "Costo de unidad"
                        }}
                    </strong>
                    <small>
                        {{ messageValue | money }}
                    </small>
                </div>
                <div class="w-1/2 lg:w-4/12 mt-3 flex flex-col">
                    <strong class="text-sm">Nuevo Margen Burto</strong>
                    <small
                        :class="{
                            'text-theme-primary': revenue > 0.3,
                            'text-danger': revenue < 0.08
                        }"
                    >
                        {{ revenue | percent }}
                    </small>
                </div>
            </div>

            <div class="flex flex-wrap">
                <div class="w-full lg:w-1/2 mt-6 pr-0 lg:pr-2">
                    <custom-input
                        v-model="quantity"
                        min="1"
                        type="number"
                        :disabled="!enabled"
                        label="Stock"
                        class="w-full"
                        @input="calculateUnitCost"
                        @focus="onFocus"
                        @blur="onBlur"
                    />
                </div>

                <div
                    class="w-full lg:w-1/2 mt-6 pl-0 lg:pl-2 flex items-center"
                >
                    <div class="w-full">
                        <input-money-component
                            v-model="costTemp"
                            :enabled="enabled"
                            :label="
                                isInputCostUnit
                                    ? 'Costo de unidad'
                                    : 'Costo de lote'
                            "
                            class="w-full min-w-full"
                            @input="calculateUnitCost"
                            @focus="onFocus"
                            @blur="onBlurCostOrPrice"
                        />
                        <p
                            v-if="costWarning"
                            class="mt-1 text-xs text-theme-warning"
                        >
                            {{ costWarning }}
                        </p>
                    </div>

                    <custom-tooltip v-if="enabled">
                        <app-button
                            icon
                            border
                            class="ml-2 shrink-0 h-10"
                            :active="isInputCostUnit"
                            @click="updateUnitCostSw"
                        >
                            {{ isInputCostUnit ? "1️⃣" : "🔢" }}
                        </app-button>

                        <template #tooltip>
                            <div class="content-tooltip">
                                <span v-if="isInputCostUnit">
                                    Registrar con costo de lote
                                </span>
                                <span v-else>
                                    Registrar con costo de unidad
                                </span>
                            </div>
                        </template>
                    </custom-tooltip>
                </div>

                <div
                    v-if="reference.needRecord && requiredPrice"
                    class="w-full lg:w-1/2 mt-6 pr-0 lg:pr-2"
                >
                    <input-money-component
                        v-model="priceNew"
                        :enabled="enabled"
                        label="Precio de venta"
                        class="w-full min-w-full"
                        @input="calculateUnitCost"
                        @focus="onFocus"
                        @blur="onBlurCostOrPrice"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { DEFAULT_IMAGE_URL } from "@/common/constants";
import { getCostWarning } from "@/common/lib/price-offer";

import CustomInput from "@/components/utils/CustomInput.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";

export default {
    name: "ReferenceInventoryItems",
    components: { InputMoneyComponent, CustomInput },
    props: {
        reference: {
            type: Object,
            required: true,
            default: () => ({
                id: 20,
                size: "pequeño",
                color: "rojo",
                colorRef: "#f00",
                quantity: 1,
                cost: 0,
                costNew: 0,
                price: 10000,
                priceOffer: 10000,
                needRecord: false,
                images: [
                    {
                        id: 1,
                        url: DEFAULT_IMAGE_URL,
                        alt: "alt"
                    }
                ],
                product: {
                    id: "",
                    name: "",
                    summary: ""
                }
            })
        },
        enabled: {
            type: Boolean,
            required: false,
            default: () => true
        },
        requiredPrice: {
            type: Boolean,
            required: false,
            default: () => false
        }
    },
    emits: ["update", "remove", "focus", "blur"],
    data: () => ({
        quantity: 0,
        costTemp: 0,
        priceNew: 0,
        costNew: 0,
        revenue: 0,
        isInputCostUnit: true,
        messageValue: 0,
        revenueCurrent: 0
    }),
    computed: {
        costWarning() {
            return getCostWarning({
                salePrice: this.priceNew,
                cost: this.costNew,
                offerActive: false
            });
        }
    },
    watch: {
        reference(value) {
            this.setInput(value);
        }
    },
    mounted() {
        const costNew =
            this.reference.costNew ||
            this.reference.cost ||
            this.reference.priceOffer * 0.7;
        this.quantity = Number(this.reference.quantity);
        this.costTemp = +costNew;
        this.costNew = +costNew;
        this.messageValue = Number(costNew);
        this.revenue = 1 - costNew / this.reference.priceOffer;
        this.revenueCurrent =
            1 - this.reference.cost / this.reference.priceOffer;
        this.setInput(this.reference);
        this.changeReference();
    },
    methods: {
        goToMainPage(slug) {
            window.open(`${this.$publicUrl}/${slug}`, "_blank").focus();
        },
        remove() {
            this.$emit("remove", { id: this.reference.id });
        },
        applyCost() {
            if (this.isInputCostUnit) {
                this.costNew = +this.costTemp;
            } else {
                this.costNew = this.costTemp / this.quantity;
            }
        },
        updateUnitCostSw() {
            this.applyCost();
            this.isInputCostUnit = !this.isInputCostUnit;
            this.calculateUnitCost();
        },
        calculateUnitCost() {
            this.applyCost();
        },
        changeReference() {
            this.$emit("update", {
                id: this.reference.id,
                costNew: this.costNew,
                priceNew: this.priceNew,
                quantity: this.quantity
            });
        },
        setInput(value) {
            this.quantity = +value.quantity;
            this.messageValue = this.isInputCostUnit
                ? value.costNew * value.quantity
                : +value.costNew;
            const costNew =
                value.costNew || value.cost || value.priceOffer * 0.7;
            this.priceNew = +value.priceNew || value.priceOffer;
            this.applyCost();
            this.costNew = +costNew;
            this.revenue = 1 - this.costNew / this.priceNew;
        },
        onFocus() {
            this.$emit("focus");
        },
        onBlur() {
            this.$emit("blur");
            this.changeReference();
        },
        onBlurCostOrPrice() {
            this.onBlur();
        }
    }
};
</script>
